import {
  Avatar,
  Link,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowTrendingLines24Regular,
  Building16Regular,
  Call16Regular,
  Globe16Regular,
  Location16Regular,
  Mail16Regular,
  People16Regular,
  PeopleTeam24Regular,
  Phone16Regular,
  Target24Regular,
  Trophy24Regular,
} from '@fluentui/react-icons';
import { QueryAggregate, Routes, readColumnRawValue } from '@powerportalspro/core';
import {
  LookupRecordContext,
  RecordContext,
  useRecordContext,
  useT,
} from '@powerportalspro/react';
import { Calendar, NewAppointmentCalendarButton } from '@powerportalspro/react-calendar';
import { AggregateDataverseChartDataSource, ChartType } from '@powerportalspro/react-charts';
import {
  AddressDisplayValue,
  BoolEdit,
  ColumnDisplayValue,
  ColumnEdit,
  DataverseChart,
  DeleteContextButton,
  DescriptionLocation,
  ExportGridButton,
  GridColumnMenuItem,
  GridLayout,
  GridToolbarItems,
  MenuBar,
  NavigateNewRecordGridButton,
  NavigateOpenRecordGridButton,
  RefreshContextButton,
  SaveContextButton,
  Section,
  SectionColumn,
  SubGrid,
  SummaryCard,
  SummaryCards,
  TabPanel,
  Tabs,
  Timeline,
} from '@powerportalspro/react-fluent';
import { useMemo, useState, type ReactElement } from 'react';
import {
  DetailContactCard,
  HeroFactText,
  heroBand,
  initials,
  RecordDocuments,
  recordContextTarget,
  type DetailPageProps,
  type HeroFactKind,
} from './DetailPageParts';
import {
  ACCOUNT_CONTACTS_VIEW_ID,
  CLOSED_OPPORTUNITIES_FETCH_XML,
  CLOSED_OPPORTUNITIES_VIEW_ID,
  OPEN_OPPORTUNITIES_FETCH_XML,
  OPEN_OPPORTUNITIES_VIEW_ID,
  OPPORTUNITIES_VIEW_ID,
  WON_OPPORTUNITIES_FETCH_XML,
  getAccountContactsView,
  getAccountDocumentsView,
  getCalendarViews,
  getClosedOpportunitiesView,
  getOpenOpportunitiesView,
  getOpportunitiesView,
} from './DetailPageRecords';
import { OpportunitiesWonByMonthChart } from './OpportunitiesWonByMonthChart';

// The account page: the one the grids open, and the Account 360 demo. One account, the whole
// picture on one page: a header with its logo, name and where it is, drawn from the record the
// RecordContext loaded; four figures across the top, each a SummaryCard over the account's related
// records, which Dataverse works out and the server formats — the pipeline card opens the
// opportunities tab, and the contacts card the contacts tab; and tabs for an overview with the
// primary contact (read through a LookupRecordContext and drawn from display values) and two
// charts, its contacts as cards the page draws (DetailContactCard), its opportunities in three
// views — open, closed, and all of them grouped by status — with totals, its notes and portal
// messages in a Timeline, its appointments in a Calendar, its documents in a FileGrid to preview
// and download, and the rest of its details.
//
// A new account (no id in the address) shows only its fields: the header, the figures and the
// related records come once it is saved. The grids' Open and New buttons find the contact and
// opportunity pages registered with AddRecordPage, so they name no address.
//
// The documents are the ppp_file rows whose Account lookup points at this account. A file dropped
// on the grid is created with that lookup set, through onUploadRecordAsync. The form tabs stay
// mounted while hidden, so an edit made on one is still saved from the other; the grids mount when
// their tab is first opened.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.DetailPages.AccountDetail';

const CONTACT_URL = 'demos/contact';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
  },
  hero: {
    ...heroBand,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXL,
    padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalXXL}`,
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '80px',
    height: '80px',
    borderRadius: tokens.borderRadiusXLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightSemibold,
    overflow: 'hidden',
    boxShadow: tokens.shadow4,
  },
  logoImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  heroText: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
    minWidth: 0,
  },
  eyebrow: {
    fontSize: tokens.fontSizeBase200,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    opacity: 0.85,
  },
  name: {
    margin: 0,
    fontSize: tokens.fontSizeHero800,
    lineHeight: tokens.lineHeightHero800,
    fontWeight: tokens.fontWeightSemibold,
  },
  facts: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
  },
  fact: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase300,
  },
  panel: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalL,
  },
  // The primary contact, as a card beside the charts.
  contact: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalM,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
    border: `${tokens.strokeWidthThin} solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusXLarge,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    minWidth: 0,
  },
  contactEyebrow: {
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  contactHeader: {
    display: 'flex',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalM,
  },
  contactIdentity: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  contactName: {
    fontSize: tokens.fontSizeBase500,
    lineHeight: tokens.lineHeightBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  contactTitle: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  contactDetails: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXS,
  },
  contactDetail: {
    display: 'flex',
    alignItems: 'flex-start',
    columnGap: tokens.spacingHorizontalS,
    minWidth: 0,
    color: tokens.colorNeutralForeground2,
    '& > svg': {
      flexShrink: 0,
      marginTop: tokens.spacingVerticalXS,
    },
  },
  // Three facts with their labels, side by side, on a tinted strip.
  contactFacts: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  charts: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: tokens.spacingHorizontalXL,
  },
});

/** The account page: a fixed account with `id`, or the one in the address with `queryParameterName`. */
export function AccountDetail(props: DetailPageProps) {
  return (
    <RecordContext table="account" {...recordContextTarget(props)} forceSuccessfulValidationBeforeSave>
      <AccountPage />
    </RecordContext>
  );
}

function AccountPage() {
  const styles = useStyles();
  const t = useT();
  const { record } = useRecordContext();
  // A saved account: one with records of its own to show. A new one has no id until it is saved.
  const accountId = record?.id;
  const [tab, setTab] = useState<string>();

  return (
    <div className={styles.root}>
      {accountId && (
        <>
          <AccountHero />
          <AccountFigures onOpenTab={setTab} />
        </>
      )}

      <MenuBar>
        <SaveContextButton />
        <RefreshContextButton />
        <DeleteContextButton />
      </MenuBar>

      {/* The selected tab is kept in ?tab=. The form's tabs mount with the page, so it validates
          and saves them unopened; the grids' each load when their tab is first opened. */}
      <Tabs selectedTabQueryParameter="tab" {...(tab && { selectedTab: tab })} onSelectedTabChange={setTab}>
        <TabPanel value="overview" header={t(`${STRINGS_BASE}.tab-overview`)} lazyMount={false}>
          <div className={styles.panel}>
            {accountId && <AccountCharts key={accountId} accountId={accountId} />}
            <Section columnCount={3}>
              <SectionColumn headerText={t(`${STRINGS_BASE}.section-company`)}>
                <ColumnEdit columnName="name" />
                <ColumnEdit columnName="accountnumber" />
                <ColumnEdit columnName="telephone1" />
                <ColumnEdit columnName="fax" />
                <ColumnEdit columnName="websiteurl" />
                <ColumnEdit columnName="parentaccountid" />
                <ColumnEdit columnName="tickersymbol" />
              </SectionColumn>
              <SectionColumn headerText={t(`${STRINGS_BASE}.section-address`)}>
                <ColumnEdit columnName="address1_line1" />
                <ColumnEdit columnName="address1_line2" />
                <ColumnEdit columnName="address1_line3" />
                <ColumnEdit columnName="address1_city" />
                <ColumnEdit columnName="address1_stateorprovince" />
                <ColumnEdit columnName="address1_postalcode" />
                <ColumnEdit columnName="address1_country" />
              </SectionColumn>
              <SectionColumn headerText={t(`${STRINGS_BASE}.section-profile`)}>
                <ColumnEdit columnName="industrycode" />
                <ColumnEdit columnName="sic" />
                <ColumnEdit columnName="ownershipcode" />
                <ColumnEdit columnName="numberofemployees" />
                <ColumnEdit columnName="revenue" />
                <ColumnEdit columnName="primarycontactid" />
              </SectionColumn>
            </Section>
          </div>
        </TabPanel>

        {/* Keyed by the account, so another one starts its grids afresh. */}
        {accountId && (
          <>
            <TabPanel value="contacts" header={t(`${STRINGS_BASE}.tab-contacts`)}>
              <div className={styles.panel}>
                <AccountContacts key={accountId} accountId={accountId} />
              </div>
            </TabPanel>
            <TabPanel value="opportunities" header={t(`${STRINGS_BASE}.tab-opportunities`)}>
              <div className={styles.panel}>
                <AccountOpportunities key={accountId} />
              </div>
            </TabPanel>
            <TabPanel value="timeline" header={t(`${STRINGS_BASE}.tab-timeline`)}>
              <div className={styles.panel}>
                <Timeline key={accountId} allowFiltering allowSavedViews savedViewsKey="account-detail-timeline" />
              </div>
            </TabPanel>
            <TabPanel value="calendar" header={t(`${STRINGS_BASE}.tab-calendar`)}>
              <div className={styles.panel}>
                <AccountCalendar key={accountId} accountId={accountId} />
              </div>
            </TabPanel>
            <TabPanel value="documents" header={t(`${STRINGS_BASE}.tab-documents`)}>
              <div className={styles.panel}>
                <AccountDocuments key={accountId} accountId={accountId} />
              </div>
            </TabPanel>
          </>
        )}

        <TabPanel value="details" header={t(`${STRINGS_BASE}.tab-details`)} lazyMount={false}>
          <div className={styles.panel}>
            <Section columnCount={3}>
              <SectionColumn headerText={t(`${STRINGS_BASE}.section-description`)}>
                <ColumnEdit columnName="description" />
              </SectionColumn>
              <SectionColumn headerText={t(`${STRINGS_BASE}.section-billing`)}>
                <ColumnEdit columnName="creditlimit" />
                <BoolEdit columnName="creditonhold" inlineLabel={false} />
                <ColumnEdit columnName="paymenttermscode" />
                <ColumnEdit columnName="transactioncurrencyid" />
                <ColumnEdit columnName="aging30" descriptionLocation={DescriptionLocation.None} />
                <ColumnEdit columnName="aging60" descriptionLocation={DescriptionLocation.None} />
                <ColumnEdit columnName="aging90" descriptionLocation={DescriptionLocation.None} />
                <ColumnEdit columnName="address1_shippingmethodcode" />
                <ColumnEdit columnName="address1_freighttermscode" />
              </SectionColumn>
              <SectionColumn headerText={t(`${STRINGS_BASE}.section-preferences`)}>
                <ColumnEdit columnName="preferredcontactmethodcode" />
                <ColumnEdit columnName="donotemail" />
                <ColumnEdit columnName="followemail" />
                <ColumnEdit columnName="donotbulkemail" />
                <ColumnEdit columnName="donotphone" />
                <ColumnEdit columnName="donotfax" />
                <ColumnEdit columnName="donotpostalmail" />
              </SectionColumn>
            </Section>
          </div>
        </TabPanel>
      </Tabs>
    </div>
  );
}

// The account's logo, name and where it is, from the loaded record.
function AccountHero() {
  const styles = useStyles();
  const t = useT();
  const { record } = useRecordContext();
  const formatted = record?.formattedValues ?? {};
  const name = readColumnRawValue<string>(record, 'name') ?? '';

  // The image route takes the image's timestamp as its version, so the browser keeps the logo
  // until it changes.
  const version = readColumnRawValue<number | string>(record, 'entityimage_timestamp');
  const logoUrl =
    record?.id && version != null
      ? Routes.api.getRetrieveImageRoute('account', record.id, 'entityimage', version, 160)
      : null;

  const employees = formatted['numberofemployees'];
  const facts: [ReactElement, string | null | undefined, HeroFactKind?][] = [
    [<Location16Regular key="city" />, readColumnRawValue<string>(record, 'address1_city'), 'address'],
    [<Building16Regular key="industry" />, formatted['industrycode']],
    [<People16Regular key="employees" />, employees ? t(`${STRINGS_BASE}.employees`, [employees]) : null],
    [<Globe16Regular key="website" />, readColumnRawValue<string>(record, 'websiteurl'), 'website'],
  ];

  return (
    <header className={styles.hero}>
      <div className={styles.logo}>
        {logoUrl ? <img className={styles.logoImage} src={logoUrl} alt="" /> : <span>{initials(name)}</span>}
      </div>
      <div className={styles.heroText}>
        <span className={styles.eyebrow}>{t(`${STRINGS_BASE}.eyebrow`)}</span>
        <h2 className={styles.name}>{name}</h2>
        <div className={styles.facts}>
          {facts
            .filter(([, text]) => !!text)
            .map(([icon, text, kind]) => (
              <span key={icon.key} className={styles.fact}>
                {icon}
                <HeroFactText text={text!} kind={kind} mapTitle={name} />
              </span>
            ))}
        </div>
      </div>
    </header>
  );
}

// The figures across the top, each a SummaryCard over the account's related records, which
// Dataverse works out and the server formats. The pipeline card opens the opportunities tab, and the
// contacts card the contacts tab.
function AccountFigures({ onOpenTab }: { onOpenTab: (tab: string) => void }) {
  const t = useT();
  return (
    <SummaryCards>
      <SummaryCard
        icon={<ArrowTrendingLines24Regular />}
        label={t(`${STRINGS_BASE}.kpi-pipeline`)}
        relationshipName="opportunity_customer_accounts"
        fetchXml={OPEN_OPPORTUNITIES_FETCH_XML}
        columnName="estimatedvalue"
        aggregate={QueryAggregate.Sum}
        format="C0"
        caption={t(`${STRINGS_BASE}.open-deals`)}
        captionAggregate={QueryAggregate.Count}
        onClick={() => onOpenTab('opportunities')}
      />
      <SummaryCard
        icon={<Trophy24Regular />}
        label={t(`${STRINGS_BASE}.kpi-won`)}
        relationshipName="opportunity_customer_accounts"
        fetchXml={WON_OPPORTUNITIES_FETCH_XML}
        columnName="actualvalue"
        aggregate={QueryAggregate.Sum}
        format="C0"
        caption={t(`${STRINGS_BASE}.won-deals`)}
        captionAggregate={QueryAggregate.Count}
      />
      <SummaryCard
        icon={<Target24Regular />}
        label={t(`${STRINGS_BASE}.kpi-win-rate`)}
        relationshipName="opportunity_customer_accounts"
        fetchXml={WON_OPPORTUNITIES_FETCH_XML}
        ofFetchXml={CLOSED_OPPORTUNITIES_FETCH_XML}
        aggregate={QueryAggregate.Count}
      />
      <SummaryCard
        icon={<PeopleTeam24Regular />}
        label={t(`${STRINGS_BASE}.kpi-contacts`)}
        relationshipName="contact_customer_accounts"
        aggregate={QueryAggregate.Count}
        onClick={() => onOpenTab('contacts')}
      />
    </SummaryCards>
  );
}

// The primary contact's details under their name, each behind its icon; one they have no value for
// is left out.
const CONTACT_DETAILS: readonly { column: string; icon: ReactElement }[] = [
  { column: 'emailaddress1', icon: <Mail16Regular /> },
  { column: 'telephone1', icon: <Call16Regular /> },
  { column: 'mobilephone', icon: <Phone16Regular /> },
];

// The primary contact, read through the account's lookup to them: LookupRecordContext gives them a
// record context of their own, which the card reads, drawing them from display values.
function PrimaryContact() {
  return (
    <LookupRecordContext columnName="primarycontactid">
      <PrimaryContactCard />
    </LookupRecordContext>
  );
}

function PrimaryContactCard() {
  const styles = useStyles();
  const t = useT();
  const { record: contact } = useRecordContext();
  // Drawn only with a contact: an account without one shows no card.
  if (!contact?.id) return null;

  const name = readColumnRawValue<string>(contact, 'fullname') ?? '';
  // The contact's photo, versioned by its timestamp as the header's logo is; none shows initials.
  const version = readColumnRawValue<number | string>(contact, 'entityimage_timestamp');
  const image = version != null ? Routes.api.getRetrieveImageRoute('contact', contact.id, 'entityimage', version, 112) : null;

  const hasAddress = ['address1_composite', 'address1_line1', 'address1_city'].some(
    (column) => readColumnRawValue(contact, column) != null,
  );

  return (
    <section className={styles.contact}>
      <span className={styles.contactEyebrow}>{t(`${STRINGS_BASE}.primary-contact`)}</span>
      <div className={styles.contactHeader}>
        <Avatar
          size={56}
          color="colorful"
          name={name}
          {...(image && { image: { src: image } })}
        />
        <div className={styles.contactIdentity}>
          <span className={styles.contactName}>{name}</span>
          <span className={styles.contactTitle}>{readColumnRawValue<string>(contact, 'jobtitle')}</span>
        </div>
      </div>
      <div className={styles.contactDetails}>
        {CONTACT_DETAILS.filter(({ column }) => readColumnRawValue(contact, column) != null).map(({ column, icon }) => (
          <div key={column} className={styles.contactDetail}>
            {icon}
            <ColumnDisplayValue columnName={column} displayLabelWhenAvailable={false} descriptionLocation={DescriptionLocation.None} />
          </div>
        ))}
        {hasAddress && (
          <div className={styles.contactDetail}>
            <Location16Regular />
            <AddressDisplayValue displayLabelWhenAvailable={false} descriptionLocation={DescriptionLocation.None} />
          </div>
        )}
      </div>
      <div className={styles.contactFacts}>
        <ColumnDisplayValue
          columnName="preferredcontactmethodcode"
          label={t(`${STRINGS_BASE}.contact-method`)}
          descriptionLocation={DescriptionLocation.None}
        />
        <ColumnDisplayValue
          columnName="preferredappointmenttimecode"
          label={t(`${STRINGS_BASE}.contact-time`)}
          descriptionLocation={DescriptionLocation.None}
        />
        <ColumnDisplayValue
          columnName="birthdate"
          label={t(`${STRINGS_BASE}.contact-birthday`)}
          descriptionLocation={DescriptionLocation.None}
        />
      </div>
      <Link href={`${CONTACT_URL}?contactId=${contact.id}`}>{t(`${STRINGS_BASE}.contact-open`)}</Link>
    </section>
  );
}

// The account's won revenue by month, and how many of its opportunities are open, won and lost.
function AccountCharts({ accountId }: { accountId: string }) {
  const styles = useStyles();
  const t = useT();
  const byStatus = useMemo(
    () =>
      new AggregateDataverseChartDataSource({
        tableName: 'opportunity',
        groupByColumn: 'statecode',
        aggregateColumn: 'opportunityid',
        aggregate: QueryAggregate.Count,
        filterXml: `<filter><condition attribute='customerid' operator='eq' value='${accountId}' /></filter>`,
      }),
    [accountId],
  );

  return (
    <div className={styles.charts}>
      <PrimaryContact />
      <OpportunitiesWonByMonthChart lookupColumn="customerid" />
      <DataverseChart
        dataSource={byStatus}
        type={ChartType.Doughnut}
        title={t(`${STRINGS_BASE}.chart-by-status`)}
        height="320px"
      />
    </div>
  );
}

// Everything the column menu offers but totals: contacts have nothing worth totaling, so the
// cards' Totals button is left off.
const NO_TOTALS = Object.values(GridColumnMenuItem).filter((item) => item !== GridColumnMenuItem.Totals);

// Cards only, each drawn by the page.
function AccountContacts({ accountId }: { accountId: string }) {
  const t = useT();
  const views = useMemo(() => [getAccountContactsView(accountId, t(`${STRINGS_BASE}.view-contacts`))], [accountId, t]);
  return (
    <SubGrid
      tableName="contact"
      relationshipName="contact_customer_accounts"
      defaultLayout={GridLayout.Cards}
      cardImageColumn="entityimage"
      cardRenderer={(card) => <DetailContactCard card={card} />}
      minCardWidth="280px"
      maxHeight="520px"
      columnMenuItems={NO_TOTALS}
      customViewDefinitions={views}
      viewIds={[ACCOUNT_CONTACTS_VIEW_ID]}
      defaultViewId={ACCOUNT_CONTACTS_VIEW_ID}
    >
      <GridToolbarItems>
        <NavigateNewRecordGridButton />
        <NavigateOpenRecordGridButton />
      </GridToolbarItems>
    </SubGrid>
  );
}

function AccountOpportunities() {
  const t = useT();
  const views = useMemo(
    () => [
      getOpenOpportunitiesView(t(`${STRINGS_BASE}.view-open-opportunities`)),
      getClosedOpportunitiesView(t(`${STRINGS_BASE}.view-closed-opportunities`)),
      getOpportunitiesView(t(`${STRINGS_BASE}.view-opportunities`)),
    ],
    [t],
  );
  return (
    <SubGrid
      tableName="opportunity"
      relationshipName="opportunity_customer_accounts"
      maxHeight="520px"
      customViewDefinitions={views}
      viewIds={[OPEN_OPPORTUNITIES_VIEW_ID, CLOSED_OPPORTUNITIES_VIEW_ID, OPPORTUNITIES_VIEW_ID]}
      defaultViewId={OPPORTUNITIES_VIEW_ID}
      persistedStateQueryParameter="opportunitiesState"
    >
      <GridToolbarItems>
        <NavigateNewRecordGridButton />
        <NavigateOpenRecordGridButton />
        <ExportGridButton />
      </GridToolbarItems>
    </SubGrid>
  );
}

// The account's appointments, tasks and phone calls.
function AccountCalendar({ accountId }: { accountId: string }) {
  const t = useT();
  const regarding = useMemo(() => ({ tableName: 'account', id: accountId }), [accountId]);
  const views = useMemo(
    () => getCalendarViews(t(`${STRINGS_BASE}.view-activities`), t(`${STRINGS_BASE}.view-to-do`)),
    [t],
  );
  return (
    <Calendar
      regarding={regarding}
      defaultLayout="month"
      height="640px"
      customViewDefinitions={views}
      allowFiltering
      allowSavedViews
      savedViewsKey="account-detail-calendar"
      persistedStateQueryParameter="calendarState"
      editable
    >
      <NewAppointmentCalendarButton />
    </Calendar>
  );
}

// The account's documents: the files whose Account lookup points at it.
function AccountDocuments({ accountId }: { accountId: string }) {
  const t = useT();
  const view = useMemo(
    () => getAccountDocumentsView(accountId, t(`${STRINGS_BASE}.view-documents`)),
    [accountId, t],
  );
  return <RecordDocuments view={view} lookupColumn="ppp_accountid" table="account" recordId={accountId} />;
}
